import { useFormik } from "formik";
import * as Yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { submitSupportRequestAPI } from "../../api/user";
import useToast from "../../hooks/useToast";
import GreenBtn from "../button/SecondaryBtn";
import { useDispatch } from "react-redux";
import { closeModal } from "../../redux/slices/uiSlice";
import { useTranslation } from "react-i18next";

const textareaClasses =
  "2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full  lg:text-[1.1vw]/none md:text-base/none text-sm/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent resize-none hide-scroll";

function SupportModal() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const validationSchema = Yup.object().shape({
    description: Yup.string()
      .required(t("supportModal.pleaseDescribeYourIssueOrQuestion"))
      .test("not-only-spaces", t("supportModal.pleaseDescribeYourIssueOrQuestion"), (value) => {
        return value ? value.trim().length > 0 : false;
      })
      .test("min-length", t("supportModal.descriptionMustBeAtLeast10Characters"), (value) => {
        return value ? value.trim().length >= 10 : false;
      })
      .max(1000, t("supportModal.descriptionMustBeAtMost1000Characters")),
  });

  const { mutate, isPending } = useMutation({
    mutationFn: submitSupportRequestAPI,
    onSuccess: (data) => {
      useToast(data?.message || t("supportModal.supportRequestSubmittedSuccessfully"), "success");
      resetForm();
      dispatch(closeModal());
    },
    onError: (error: any) => {
      useToast(error.message || t("supportModal.failedToSubmitSupportRequest"), "error");
    },
  });

  const { handleSubmit, values, errors, touched, handleChange, handleBlur, resetForm } = useFormik({
    initialValues: {
      description: "",
    },
    validationSchema,
    onSubmit: (values) => {
      mutate({
        description: values.description.trim(),
      });
    },
  });

  const handleClose = () => {
    resetForm();
    dispatch(closeModal());
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center lg:p-[1%] p-4 z-50">
      {/* Modal Content */}
      <div className="modal bg-white-500 lg:rounded-[1.5vw] rounded-3xl 2xl:p-[2vw] md:p-8 p-6 2xl:max-w-[35vw] md:max-w-[600px] max-w-[90%] w-full relative animate-[slideUp_0.3s_ease-out] lg:border-[0.2vw] border-3 border-primary auth-page overflow-auto lg:max-h-[calc(100dvh-11%)] max-h-[calc(100dvh-40px)] hide-scroll">
        {/* Header */}
        <div className="flex 2xl:gap-[1vw] gap-2 items-start justify-between w-full lg:mb-[1vw] mb-3">
          <h1 className="2xl:text-[1.563vw]/[1.7vw] xl:text-3xl/tight text-2xl/6 font-semibold text-primary-orange">
            {t("supportModal.support")}
          </h1>
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="lg:w-[1.2vw] lg:h-[1.2vw] md:w-5 w-4 md:h-5 h-4 flex items-center justify-center cursor-pointer ms-auto hover:scale-110 transition-transform duration-300 outline-none"
          >
            <svg
              className="w-full h-full"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.9474 12.1123C15.8856 12.0504 15.8856 11.9496 15.9474 11.8878L23.5029 4.3286C24.149 3.68231 24.149 2.63063 23.5029 1.98429L22.0051 0.48573C21.692 0.172468 21.2758 0 20.833 0C20.3902 0 19.9739 0.172468 19.6609 0.485683L12.1054 8.04493C12.0672 8.08316 12.0235 8.09122 11.9937 8.09122C11.9639 8.09122 11.9203 8.08321 11.8821 8.04493L4.32658 0.485683C4.01351 0.172468 3.59726 0 3.15445 0C2.71164 0 2.29544 0.172468 1.98236 0.485683L0.484547 1.98429C-0.161516 2.63063 -0.161516 3.68231 0.484547 4.3286L8.04001 11.8878C8.10185 11.9496 8.10185 12.0504 8.04001 12.1123L0.484547 19.6714C-0.161469 20.3178 -0.161469 21.3694 0.484547 22.0157L1.98236 23.5143C2.29544 23.8275 2.71168 24 3.15445 24C3.59721 24 4.01351 23.8275 4.32658 23.5143L11.882 15.9551C11.9203 15.9169 11.9638 15.9089 11.9937 15.9089C12.0235 15.9089 12.0671 15.9169 12.1053 15.9552L19.6608 23.5143C19.9739 23.8276 20.3901 24 20.8329 24C21.2758 24 21.692 23.8276 22.005 23.5143L23.5028 22.0158C24.1489 21.3695 24.1489 20.3178 23.5028 19.6715L15.9474 12.1123Z"
                fill="#333938"
              />
            </svg>
          </button>
        </div>

        {/* Divider */}
        <div className="text-center lg:mb-[1.4vw] md:mb-6 mb-4">
          <hr className="border-0 2xl:h-[0.053vw] h-px w-full 2xl:mb-[1vw] md:mb-4 mb-3 2xl:mt-[0.5vw] mt-2 [background:linear-gradient(90deg,rgba(11,51,105,0)_0%,#7364FF_50%,rgba(11,51,105,0)_100%)]" />
          <p className="lg:text-[1.1vw]/[1.4vw] text-base/tight text-black-800 font-bold">
            {t("supportModal.needHelpDescribeYourIssueAndWeWillGetBackToYou")}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="2xl:space-y-[1vw] space-y-4">
          {/* Description Field */}
          <div>
            <label className="block 2xl:text-[0.85vw] text-sm font-medium text-primary-orange 2xl:mb-[0.4vw] mb-2">
              {t("supportModal.howCanWeHelpYou")} <span className="text-red-500">*</span>
            </label>
            <div className={`relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex
    2xl:rounded-[0.26vw] rounded-[5px]
    skew-x-[-10deg]
    2xl:border-[0.104vw] border-[1.5px]
    transition-all border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]
  `}>
              <textarea
                name="description"
                placeholder={t("supportModal.pleaseDescribeYourIssueQuestionOrFeedbackInDetail")}
                value={values.description}
                onChange={(e) => {
                  const trimmedValue = e.target.value.trimStart();
                  handleChange({ target: { name: 'description', value: trimmedValue } });
                }}
                onBlur={handleBlur}
                className={textareaClasses}
                rows={6}
                maxLength={1000}
              />

              <span className="absolute right-3 lg:right-[0.8vw] bottom-5 lg:bottom-[1.5vw] translate-y-1/2 text-black-400 lg:text-[1vw] text-xs pointer-events-none ">
                {values.description.length}/1000
              </span>
            </div>
            {touched.description && errors.description && (
              <p className="text-red-500 lg:text-[0.729vw] text-sm lg:mt-[0.3vw] mt-1 lg:px-[0.833vw] px-4">
                {errors.description}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div className="2xl:pt-[0.5vw] pt-2">
            <button
              type="submit" disabled={isPending}
              className="w-full 2xl:rounded-[0.26vw] rounded-[5px] h-full flex
    skew-x-[-10deg]
    2xl:border-[0.104vw] border-[1.5px] lg:text-[1.1vw]/[1.4vw] md:text-lg/tight text-base/tight font-bold text-primary-yellow bg-primary-yellow border-black-300 transition-all duration-200 cursor-pointer text-center disabled:opacity-50 disabled:cursor-not-allowed 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
            >
              <span className="2xl:pt-[0.7vw] pt-2.5 2xl:m-[0.208vw] m-1 !mt-0 2xl:py-[0.5vw] py-2 lg:px-[5%] px-4 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[0deg] !border-t-0 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[linear-gradient(180deg,#D76A4D_0%,#B35840_101.89%)] w-full h-full"><span className="skew-x-[10deg] block">{t("supportModal.submitRequest")}</span></span>
            </button>
          </div>
        </form>

        {/* Info Text */}
        <div className="2xl:mt-[1vw] mt-4 text-center">
          <p className="lg:text-[1.1vw]/[1.4vw] text-base/tight text-black-800 font-bold">
            {t("supportModal.ourSupportTeamTypicallyRespondsWithin2448Hours")}
          </p>
        </div>
      </div>
    </div>
  );
}

export default SupportModal;
